import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { EnrichedDeviceEvent, PlayerDetail } from '../../types/gameDetail';

const TimelineContainer = styled.div`
  background: var(--legacy-card-bg);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  padding: 20px;
`;


const EventsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 500px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 212, 255, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #00d4ff;
    border-radius: 3px;
  }
`;

const EventItem = styled.div<{ $isObjectiveCompleted: boolean; $faction?: string }>`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 12px 16px;
  background: ${props => {
    if (props.$isObjectiveCompleted) return 'rgba(76, 175, 80, 0.1)';
    const f = props.$faction?.toLowerCase();
    if (f === 'alien' || f === 'aliens') return 'rgba(255, 136, 0, 0.1)';
    return 'rgba(0, 212, 255, 0.1)';
  }};
  border: 1px solid ${props => {
    if (props.$isObjectiveCompleted) return 'rgba(76, 175, 80, 0.3)';
    const f = props.$faction?.toLowerCase();
    if (f === 'alien' || f === 'aliens') return 'rgba(255, 136, 0, 0.3)';
    return 'rgba(0, 212, 255, 0.3)';
  }};
  border-radius: 8px;
  border-left: 4px solid ${props => {
    if (props.$isObjectiveCompleted) return '#4CAF50';
    const f = props.$faction?.toLowerCase();
    if (f === 'alien' || f === 'aliens') return '#ff8800';
    return '#00d4ff';
  }};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => {
      if (props.$isObjectiveCompleted) return 'rgba(76, 175, 80, 0.15)';
      const f = props.$faction?.toLowerCase();
      if (f === 'alien' || f === 'aliens') return 'rgba(255, 136, 0, 0.15)';
      return 'rgba(0, 212, 255, 0.15)';
    }};
  }
`;

const EventTime = styled.div`
  min-width: 80px;
  text-align: center;
`;

const TimeValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #00d4ff;
  font-family: 'Orbitron', sans-serif;
`;

const DateValue = styled.div`
  font-size: 10px;
  color: #8bb8d8;
  margin-top: 2px;
`;

const EventContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const EventAction = styled.div`
  font-size: 14px;
  color: #fff;
  font-weight: 500;
`;

const EventDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: #8bb8d8;
`;

const DetailItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PlayerName = styled.span<{ $isClickable: boolean }>`
  color: #00d4ff;
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;

  ${props => props.$isClickable && `
    &:hover {
      color: #fff;
      text-shadow: 0 0 8px #00d4ff;
    }
  `}
`;

const DeviceName = styled.span`
  color: #FF9800;
`;

const PointsBadge = styled.span<{ $positive: boolean }>`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => props.$positive
    ? 'rgba(76, 175, 80, 0.2)'
    : 'rgba(244, 67, 54, 0.2)'};
  color: ${props => props.$positive ? '#4CAF50' : '#F44336'};
`;

const ObjectiveCompletedBadge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
  text-transform: uppercase;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #8bb8d8;
  font-size: 14px;
`;

const FactionBadge = styled.span<{ $faction: string }>`
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    const faction = props.$faction?.toLowerCase();
    if (faction === 'human' || faction === 'humans') return '#3b82f6';
    if (faction === 'alien' || faction === 'aliens') return '#ff8800';
    return '#9E9E9E';
  }};
  color: ${props => {
    const faction = props.$faction?.toLowerCase();
    if (faction === 'human' || faction === 'humans') return '#000';
    return '#fff';
  }};
  text-shadow: none;
`;

const getFactionDisplayName = (faction: string): string => {
  const f = faction?.toLowerCase();
  if (f === 'human' || f === 'humans') return 'Human';
  if (f === 'alien' || f === 'aliens') return 'Alien';
  return faction;
};

const getFactionColor = (faction: string): string => {
  const f = faction?.toLowerCase();
  if (f === 'human' || f === 'humans') return '#3b82f6';
  if (f === 'alien' || f === 'aliens') return '#ff8800';
  return '#3b82f6';
};

interface DeviceEventTimelineProps {
  events: EnrichedDeviceEvent[];
  players?: PlayerDetail[];
}

const DeviceEventTimeline: React.FC<DeviceEventTimelineProps> = ({ events, players = [] }) => {
  const navigate = useNavigate();

  // Создаем map для быстрого поиска фракции по playerId
  const playerFactionMap = React.useMemo(() => {
    const map = new Map<string, string>();
    players.forEach(player => {
      map.set(player.userId, player.faction);
    });
    return map;
  }, [players]);

  // Форматируем время без конвертации UTC - показываем локальное время поля
  const formatTime = (timestamp: string) => {
    const match = timestamp.match(/T(\d{2}):(\d{2}):(\d{2})/);
    if (!match) return timestamp;
    return `${match[1]}:${match[2]}:${match[3]}`;
  };

  // Форматируем дату без конвертации UTC - показываем локальное время поля
  const formatDate = (timestamp: string) => {
    const match = timestamp.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return timestamp;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[parseInt(match[2], 10) - 1];
    return `${monthName} ${parseInt(match[3], 10)}`;
  };

  const handlePlayerClick = (playerId?: string) => {
    if (playerId) {
      navigate(`/profile/${playerId}`);
    }
  };

  // Sort events by timestamp (oldest first - chronological order)
  const sortedEvents = [...events].sort((a, b) =>
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  if (events.length === 0) {
    return (
      <TimelineContainer>
        <EmptyState>No events recorded for this mission</EmptyState>
      </TimelineContainer>
    );
  }

  return (
    <TimelineContainer>
      <EventsList>
        {sortedEvents.map((event, index) => {
          const playerFaction = event.playerId ? playerFactionMap.get(event.playerId) : undefined;
          return (
          <EventItem
            key={`${event.fmdId}-${event.timestamp}-${index}`}
            $isObjectiveCompleted={event.isObjectiveCompleted}
            $faction={playerFaction}
          >
            <EventTime>
              <TimeValue>{formatTime(event.timestamp)}</TimeValue>
              <DateValue>{formatDate(event.timestamp)}</DateValue>
            </EventTime>

            <EventContent>
              <EventAction>
                {event.objectiveDescription || event.commandName || event.commandCode}
                {event.commandDescription && (
                  <span style={{ color: '#8bb8d8', fontWeight: 400 }}>
                    {' '}- {event.commandDescription}
                  </span>
                )}
              </EventAction>

              <EventDetails>
                <DetailItem>
                  <span>Device:</span>
                  <DeviceName>{event.deviceName}</DeviceName>
                </DetailItem>

                {event.playerName && (
                  <>
                    <DetailItem>
                      <span>Player:</span>
                      <PlayerName
                        $isClickable={!!event.playerId}
                        onClick={() => handlePlayerClick(event.playerId)}
                      >
                        {event.playerName}
                      </PlayerName>
                    </DetailItem>
                    {event.playerId && playerFactionMap.get(event.playerId) && (
                      <FactionBadge $faction={playerFactionMap.get(event.playerId) || ''}>
                        {getFactionDisplayName(playerFactionMap.get(event.playerId) || '')}
                      </FactionBadge>
                    )}
                  </>
                )}

                {event.points > 0 && (
                  <PointsBadge $positive={true}>
                    +{event.points} pts
                  </PointsBadge>
                )}

                {event.isObjectiveCompleted && (
                  <ObjectiveCompletedBadge>
                    Objective Completed
                  </ObjectiveCompletedBadge>
                )}
              </EventDetails>

            </EventContent>
          </EventItem>
        );})}
      </EventsList>
    </TimelineContainer>
  );
};

export default DeviceEventTimeline;
